/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useState, useMemo, useEffect, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import {
  cloneDeep,
  get,
  set,
  filter,
  merge,
  omitBy,
  isUndefined,
  isArray,
  sum,
  isEmpty,
  isNumber,
} from 'lodash';
import Form from 'react-jsonschema-form';
import traverse from 'traverse';
import { diff } from 'deep-object-diff';
import { Button, ButtonGroup } from 'react-bootstrap';
// @ts-ignore @TODO: add types for package
import Toggle from 'react-bootstrap-toggle';
// @ts-ignore @TODO: add types for package
import { Confirm } from 'react-confirm-bootstrap';
import validator from 'validator';
// @ts-ignore @TODO: add types for package
import zipcodeToTimezone from 'zipcode-to-timezone';
import {
  TradeInType,
  CrmType,
  COUNTRIES,
  Condition,
  FinanceType,
  ExtendedFinanceType,
  FeeCategory,
  FixedValueFee,
  DealertrackFixedValueFees,
  FNIType,
  SpecialFees,
} from '@upstart/harmony/lib/constants';
import validation from '@upstart/harmony/lib/validation/inverted';
import {
  Dealer,
  DealerFee,
  Manager,
  AprConfigWithOptions,
  FeatureConfig,
  DealerWithFlags,
  isDealertrackFixedValueFee,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { Website, MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { Feed } from '@upstart/harmony/lib/legos/feedTypes';
import { FeedView } from '@upstart/harmony/lib/legos/feedViewTypes';
import { DealerGroup } from '@upstart/harmony/lib/legos/dealerGroupTypes';
import Theme from 'components/Website/WebsiteTheme';
import { type GetDealerSetupFeatureConfigsResponse } from 'hooks/services';
import { Feature, useGrowthBook } from 'hooks/GrowthBook';
import IntegrationVerification from '../../containers/IntegrationVerification';
import { processForm } from '../../util/jsonSchemaUtils';
import ChangeCountryChallengeModal from './ChangeCountryChallengeModal';
import { getTmpDealer } from '../../redux/dealers/selectors';
import { getWebsites } from '../../redux/websites/selectors';
import validateTradeInCredentials from '../../services/ValidationService';
import {
  getStep1Schema,
  step2Schema,
  getStep3Schema,
  getStep4Schema,
  step5Schema,
  getStep6Schema,
  getStep8Schema,
  getStep9Schema,
  step10Schema,
  ownershipOptionsSchema,
  getStep11Schema,
  dealerThemeSchema,
  InventoryImageSources,
  getV2Payloads,
  insertV2State,
  omitV2StateFromChanges,
} from './Schema';
import { ARLLendingConfig } from './ARLLendingConfig';
import {
  step1UiSchema,
  step2UiSchema,
  step3UiSchema,
  step4UiSchema,
  getStep5UiSchema,
  step6UiSchema,
  getStep8UiSchema,
  getStep9UiSchema,
  getStep10UiSchema,
  getStep11UiSchema,
} from './uiSchema';
import OwnershipOptionsConfig from './OwnershipOptionsConfig';
import './DealerEditor.scss';
import DealerLocalisationDefaults from './DealerLocalisationConstants';
import { postalCodeToTimezone } from '../../util/postalCodeToTimezone';
import { PercentField } from '../PercentField';
import { ProdigyMultiSchemaField } from '../ProdigyMultiSchemaField';
import { PageMessage } from '../GlobalUI';
import { type FormData } from './types';
import { applyUserTriggeredDefaults } from './userTriggeredDefaults';

const REDACTED_FIELD_VALUE = 'PRIVATE_PROPERTY_PLACEHOLDER';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomWidgetProps = any;

function UARCustomTextWidget(props: CustomWidgetProps) {
  const mutableProps = { ...props };
  if (mutableProps.value === REDACTED_FIELD_VALUE) {
    // copy value to placeholder and clear value
    // this is a hidden value
    mutableProps.placeholder = 'Redacted';
    mutableProps.value = '';
  }
  const { BaseInput } = props.registry.widgets;
  return (
    <BaseInput {...mutableProps} autoComplete={props.options?.autocomplete} />
  );
}

// Minimal wrapper to add support for ui:autocomplete
function UARCustomPasswordWidget(props: CustomWidgetProps) {
  const { BaseInput } = props.registry.widgets;
  return (
    <BaseInput
      {...props}
      type="password"
      autoComplete={props.options?.autocomplete}
    />
  );
}

const getSchemaList = ({
  feeds,
  formData,
  groups = [],
  feedviews,
}: {
  feeds: Feed[];
  formData: FormData;
  groups: DealerGroup[];
  feedviews: FeedView[];
}) => [
  {},
  getStep1Schema(groups, formData),
  step2Schema,
  getStep3Schema(feeds, feedviews),
  getStep4Schema(formData),
  step5Schema,
  getStep6Schema(formData),
  getStep8Schema(formData),
  getStep9Schema(),
  ownershipOptionsSchema,
  step10Schema,
  getStep11Schema(formData),
  dealerThemeSchema,
];

const getUISchemaList = (formData: FormData) => [
  {},
  step1UiSchema,
  step2UiSchema,
  step3UiSchema,
  step4UiSchema,
  getStep5UiSchema(formData),
  step6UiSchema,
  getStep8UiSchema(formData),
  getStep9UiSchema(formData.localizationConfig || {}),
  {},
  getStep10UiSchema(formData),
  getStep11UiSchema(formData),
  {},
];

const START_STEP = 1;
const FEES_STEP = 6;
const MAX_STEP = 12;
const TOTAL_STEPS = 10;

const getStep = (step: number) =>
  step >= START_STEP && step <= MAX_STEP ? step : START_STEP;

export const transformInventoryImageConfig = (
  formData: Pick<FormData, 'featureConfig'>,
): FeatureConfig['inventoryImages'] => {
  // Set inventoryImageSource flags based on selected user option.
  const chromeImageEnabled =
    formData.featureConfig?.inventoryImages?.primaryImageSource ===
    InventoryImageSources.CHROME_DATA;
  const shiftImageEnabled =
    formData.featureConfig?.inventoryImages?.primaryImageSource ===
    InventoryImageSources.SDIS;

  const inventoryImageConfig: FeatureConfig['inventoryImages'] = {
    chromeDataIsPrimaryImageSource: chromeImageEnabled,
    shiftDigitalInventorySolutionIsPrimaryImageSource: shiftImageEnabled,
  };

  return inventoryImageConfig;
};

/**
 * Normalize fee types based on fniType
 * This function normalizes the fee types if and when the fniType changes.
 */
export const normalizeFeeTypes = (
  formData: FormData,
  fniType: string | undefined,
): FormData => {
  const normalized = cloneDeep(formData);
  if (!normalized.fees) return normalized;

  Object.values(Condition).forEach((condition) => {
    [
      ...Object.values(FinanceType),
      ...Object.values(ExtendedFinanceType),
    ].forEach((financeType) => {
      Object.values(FeeCategory).forEach((feeCategory) => {
        const fees =
          get(normalized.fees, [condition, financeType, feeCategory]) || [];
        fees.forEach((fee: DealerFee) => {
          if (
            fee.type &&
            Object.values(SpecialFees).some((t) => t === fee.type)
          ) {
            // Special fees (e.g. flDocStampFee) are provider-agnostic —
            // leave them as-is regardless of fniType.
            return;
          }
          if (fniType === FNIType.DEALERTRACK) {
            if (!fee.type || fee.type === FixedValueFee) {
              fee.type = DealertrackFixedValueFees.FIXED_VALUE_BACKEND;
            }
          } else {
            // Change-of-provider case: coerce DealerTrack fixed-value fees
            // (and legacy/untyped) back to FixedValueFee.
            fee.type = FixedValueFee;
          }
        });
      });
    });
  });

  return normalized;
};

/**
 * Transform the form data result from the react-jsonschema-form Form to save
 */
export const transformFormDataForSubmit = (formData: FormData): FormData => {
  const dealerForSubmit = cloneDeep(formData);

  // react-jsonschema-form doesn't clean up hidden values
  if (dealerForSubmit.fees) {
    Object.values(Condition).forEach((condition) => {
      [
        ...Object.values(FinanceType),
        ...Object.values(ExtendedFinanceType),
      ].forEach((financeType) => {
        Object.values(FeeCategory).forEach((feeCategories) => {
          const fees =
            get(dealerForSubmit.fees, [
              condition,
              financeType,
              feeCategories,
            ]) || [];
          if (fees.length) {
            fees.forEach((fee: DealerFee) => {
              if (!fee.type || fee.type === FixedValueFee) {
                fee.type = FixedValueFee;
                // @ts-ignore
                delete fee.apr;
              } else if (isDealertrackFixedValueFee(fee)) {
                // @ts-ignore
                delete fee.apr;
              } else {
                // @ts-ignore
                delete fee.value;
              }
            });
          }
        });
      });
    });
  }

  if (!dealerForSubmit.inStoreFeedview_id) {
    dealerForSubmit.inStoreFeedview_id = null;
    dealerForSubmit.inStoreFeedView = null;
  }

  if (!dealerForSubmit.onlineFeedview_id) {
    dealerForSubmit.onlineFeedview_id = null;
    dealerForSubmit.onlineFeedView = null;
  }

  if (dealerForSubmit.featureConfig) {
    dealerForSubmit.featureConfig.inventoryImages =
      transformInventoryImageConfig(dealerForSubmit);
  }

  return dealerForSubmit;
};

/**
 * Generates a diff of a current Dealer object and a new Dealer object, returning an object with only what was changed
 * @param currentDealer
 * @param newDealer
 */
export const getDealerDiff = (currentDealer: Dealer, newDealer: Dealer) => {
  const dealerDiff = omitBy(diff(currentDealer, newDealer), isUndefined);
  const propertyPathsThatAreKnownArrays: string[] = [];

  /*
  this traverse code is needed is because of how the diff is generated.
  the diff between '{ fees: [] }' and '{ fees: ['arr-item-1', 'arr-item-2'] }' would return a diff object like below
  {
    "fees": {
      "0": "arr-item-1",
      "1": "arr-item-2"
    }
  }

  but what we want to send is this
  {
    "fees": [
      "arr-item-1",
      "arr-item-2"
    ]
  }

  Below code takes care of that. Ideally we might want to find a library to do this
  */

  traverse(dealerDiff).forEach(function fn() {
    const originalDealerProperty = get(newDealer, this.path);
    const thisPath = this.path.join('.'); // Transform into dot notation for easier comparison later
    if (
      propertyPathsThatAreKnownArrays.find(
        (propertyPath) => thisPath === propertyPath,
      )
    ) {
      // path was already identified as array and processed.
      return;
    }
    if (isArray(originalDealerProperty)) {
      propertyPathsThatAreKnownArrays.push(thisPath);
      // Replace the diff object property with the array
      this.update(originalDealerProperty);
    }
  });
  return dealerDiff;
};

export interface DealerEditorProps {
  actions: {
    fniVerificationRequest: Function;
    fniVerificationReset: Function;
    modifyDealerTmpStateRequest: Function;
    websitesLightRequest: Function;
  };
  dealer: DealerWithFlags;
  featureConfigs: GetDealerSetupFeatureConfigsResponse | undefined;
  feeds: Feed[];
  fetching: boolean;
  groups: DealerGroup[];
  onSave: Function;
  setStep: Function;
  step: number;
  tmpDealer: Dealer;
  feedviews: FeedView[];
  onInventoryImport: Function;
  websites: Website[];
}

// Errors looks very similar to the dealer tree
// eslint-disable-next-line
type Errors = any;

// @TODO: type this
// eslint-disable-next-line
type Schema = any;

export const hasDuplicateDownPaymentOptions = (
  downPayment?: FeatureConfig['downPayment'],
): boolean => {
  if (downPayment?.downPaymentOptions) {
    if (
      isNumber(downPayment?.downPaymentOptions?.percentageDownPaymentA) &&
      isNumber(downPayment?.downPaymentOptions?.percentageDownPaymentB) &&
      isNumber(downPayment?.downPaymentOptions?.percentageDownPaymentC) &&
      downPayment?.downPaymentOptions?.isPercentage
    ) {
      return (
        new Set([
          downPayment?.downPaymentOptions?.percentageDownPaymentA,
          downPayment?.downPaymentOptions?.percentageDownPaymentB,
          downPayment?.downPaymentOptions?.percentageDownPaymentC,
        ]).size < 3
      );
    }

    return (
      new Set([
        downPayment?.downPaymentOptions?.fixedDownPaymentA,
        downPayment?.downPaymentOptions?.fixedDownPaymentB,
        downPayment?.downPaymentOptions?.fixedDownPaymentC,
      ]).size < 3
    );
  }

  return false;
};

const DealerEditor: FunctionComponent<DealerEditorProps> = ({
  actions,
  dealer,
  featureConfigs,
  feeds,
  fetching,
  groups,
  onSave,
  setStep,
  step,
  tmpDealer,
  feedviews,
  onInventoryImport,
  websites,
}) => {
  const { isFeatureEnabled } = useGrowthBook();
  const hideFeesTab = isFeatureEnabled(Feature.HIDE_DEALER_FEES_TAB);
  const curStep = getStep(step);
  const fniType = dealer?.fniType ?? undefined;

  const rawFormData = useMemo((): FormData => {
    const formData = insertV2State({ dealer, featureConfigs });
    return normalizeFeeTypes(formData, fniType);
  }, [dealer, featureConfigs, fniType]);

  const rawSchemaList = useMemo(
    () =>
      getSchemaList({
        feeds,
        formData: rawFormData,
        groups,
        feedviews,
      }),
    [feeds, groups, feedviews, rawFormData],
  );
  const rawUiSchemaList = useMemo(
    () => getUISchemaList(rawFormData),
    [rawFormData],
  );

  const processedFormFromProps = useMemo(
    () =>
      processForm(
        rawSchemaList[curStep],
        rawUiSchemaList[curStep],
        rawSchemaList[curStep],
        rawUiSchemaList[curStep],
        rawFormData,
      ),
    [curStep, rawSchemaList, rawUiSchemaList, rawFormData],
  );

  const [formState, setFormState] = useState(processedFormFromProps);
  const [saveAndExit, setSaveAndExit] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [formRef, setFormRef] = useState<any>();
  // eslint-disable-next-line
  const [submitBtnRef, setSubmitButtonRef] = useState();
  useEffect(() => {
    setFormState(processedFormFromProps);

    // Reset verification state
    actions.fniVerificationReset();
  }, [processedFormFromProps, actions]);

  useEffect(
    () => () => {
      actions.modifyDealerTmpStateRequest({ tmpDealer: {} });
    },
    [actions],
  );

  const populateWebsiteListIfEmpty = (enabled: boolean) => {
    if (enabled && !websites.length) {
      actions.websitesLightRequest();
    }
  };

  useEffect(() => {
    populateWebsiteListIfEmpty(!!dealer.wizard?.enableProdigyWeb?.enabled);
  }, [dealer.wizard, actions]);

  const onSubmit = async ({ formData }: { formData: FormData }) => {
    let newFormData = transformFormDataForSubmit({
      ...formState.formData,
      ...formData,
    });

    // Merge tmpDealer settings into dealer
    // These settings are not controlled by JSON Schema
    newFormData = merge(newFormData, tmpDealer);

    // function for cleaning the dealer data
    sanitizeDealer(newFormData);

    // prepare dealer diff to send the PUT request and check if credentials
    // should be validated
    const formDataDiff = getDealerDiff(rawFormData, newFormData);

    // This is required because there is no async validation with this library
    // we need to hack it to return errors and not block the ui thread with a sync call
    const tradeInManager = formData.tradeInManagerType;
    const changedLoginCredentialType =
      tradeInManager && !!formDataDiff[tradeInManager];
    if (tradeInManager && changedLoginCredentialType) {
      const credentials =
        tradeInManager === TradeInType.FIRSTLOOK
          ? formData.firstlook
          : formData?.vauto?.login;
      try {
        setIsValidating(true);
        // JIRA: REL-19707 Remove VAuto credentials check with web scraping to as VAuto relies on API now.
        if (tradeInManager !== TradeInType.VAUTO) {
          await validateTradeInCredentials(
            credentials,
            tradeInManager,
            dealer.id,
          );
        }
      } catch (e) {
        // This package is 3-4 years old, we can only rely on using the internals to add in erros
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return formRef.setState((state: any) => ({
          ...state,
          errorSchema: {
            [tradeInManager]: {
              ...state.errorSchema?.[tradeInManager],
              __errors: [`Invalid ${tradeInManager} Credentials`],
            },
            ...state.errorSchema,
          },
        }));
      } finally {
        setIsValidating(false);
      }
    }

    return onSave({
      dealer: omitV2StateFromChanges({
        ...formDataDiff,
        id: dealer.id,
      }),
      isDiffObject: true,
      // The route saga advances to step + 1, so report the Fees step as current to hop over it.
      step: hideFeesTab && curStep === FEES_STEP - 1 ? FEES_STEP : curStep,
      maxSteps: TOTAL_STEPS,
      nextStepOnSuccess: !saveAndExit,
      v2Payloads: getV2Payloads({ formData, changedData: formDataDiff }),
    });
  };

  const sanitizeDealer = (dealerObj: Dealer) => {
    // If it is empty, it gets a value of undefined and gets omitted in getDealerDiff
    if (dealerObj.supportEmail === undefined) {
      dealerObj.supportEmail = '';
    }

    // if the user reset the dropdown to empty. the value will become undefined and undefined will be ignored when we do getDealer Diff
    if (dealerObj.fniType === undefined) {
      dealerObj.fniType = null;
      dealerObj.dealertrack = null;
      dealerObj.routeone = null;
      dealerObj.cudl = null;
    }

    if (dealerObj.tradeInManagerType === undefined) {
      dealerObj.tradeInManagerType = null;
      dealerObj.firstlook = null;
      dealerObj.vauto = {};
    }

    // reset all the possible choices for crmType
    if (dealerObj.crmType === undefined) {
      dealerObj.crmType = null;
      dealerObj.vinsolutions = null;
      dealerObj.elead = null;
      dealerObj.dealersocket = null;
      dealerObj.reynolds = null;
      dealerObj.highergear = null;
      dealerObj.tekion = null;
      dealerObj.drivecentricId = null;

      // only set undefined for the otherCrmName property in the obj
      dealerObj.launchpad = { ...dealerObj.launchpad, otherCrmName: undefined };
    }

    // if Add Inception Fees to Total At Start is not checked, we need to make all the children false
    if (!dealerObj.desking?.addInceptionFeesToTotalAtStart) {
      const inceptionObj = dealerObj.desking?.inceptionFeesTotalAtStart;

      if (inceptionObj) {
        const newInceptionObj = {
          enabledByDefault: false,
          customerToggle: {
            enabled: false,
          },
          taxes: {
            enabled: false,
          },
          fees: {
            enabled: false,
            feeStatus: {
              'Doc Fee': false,
              'Tire Fee': false,
              'Electronic Filing Fee': false,
            },
          },
          firstMonth: {
            enabled: false,
          },
        };

        Object.assign(inceptionObj, newInceptionObj);
      }
    }
  };

  const onBack = () => {
    if (curStep > START_STEP) {
      const prevStep = curStep - 1;
      setStep(hideFeesTab && prevStep === FEES_STEP ? prevStep - 1 : prevStep);
    }
  };

  // Fallback for landing on the hidden tab directly (e.g. a bookmarked /6 URL).
  useEffect(() => {
    if (hideFeesTab && curStep === FEES_STEP) {
      setStep(FEES_STEP + 1);
    }
  }, [hideFeesTab, curStep, setStep]);

  const onProdigyWebsiteEnableChange = (value: boolean) => {
    set(tmpDealer, 'wizard.enableProdigyWeb.enabled', value);
    const newFormData: FormData = cloneDeep(formState.formData) as FormData;
    set(newFormData, 'wizard.enableProdigyWeb.enabled', value);

    handleChange({ formData: newFormData });
    actions.modifyDealerTmpStateRequest({ tmpDealer });
  };

  const onProdigyWebsiteChange = (value: string) => {
    set(tmpDealer, 'wizard.enableProdigyWeb.newWebsiteId', value);
    const newFormData: FormData = cloneDeep(formState.formData) as FormData;
    set(newFormData, 'wizard.enableProdigyWeb.newWebsiteId', value);

    handleChange({ formData: newFormData });
    actions.modifyDealerTmpStateRequest({ tmpDealer });
  };

  const submitForm = () => {
    setSaveAndExit(false);
    formRef.submit();
  };

  const submitAndExit = () => {
    setSaveAndExit(true);
    formRef.submit();
  };

  const updateDealerCountryDefaults = (country: COUNTRIES) =>
    DealerLocalisationDefaults[country];

  const resetDealerAddressData = (formData: FormData) => {
    formData.address = null;
    formData.city = null;
    formData.province = null;
    formData.county = null;
    formData.zip = null;
    formData.state = null;
    return formData;
  };

  const processFormHandler = (formData: FormData) => {
    const uiSchemaForProcessForm = getUISchemaList(formData);
    const schemaForProcessForm = getSchemaList({
      feeds,
      groups,
      feedviews,
      formData,
    });
    const newState = processForm(
      schemaForProcessForm[curStep],
      uiSchemaForProcessForm[curStep],
      schemaForProcessForm[curStep],
      uiSchemaForProcessForm[curStep],
      formData,
    );
    newState.formData = formData;
    setFormState(newState);
  };

  const changeCountry = (formData: FormData) => {
    formData = { ...formData, country: formData.countryToggle };
    formData = resetDealerAddressData(formData);
    formData.localizationConfig = updateDealerCountryDefaults(
      formData.country as COUNTRIES,
    );
    formData.state = '';
    if (formData.country === COUNTRIES.CA && !!formData.firstlook) {
      formData.firstlook = null;
    }
    if (formData?.desking?.showBiweeklyPayments) {
      formData.desking.showBiweeklyPayments = formData.country === COUNTRIES.CA;
    }
    processFormHandler(formData);
  };

  const thirdPartyLendersEnabled = (
    aprConfigOptionsNew: AprConfigWithOptions | null | undefined,
    inStoreAprConfigOptionsNew: AprConfigWithOptions | null | undefined,
  ) =>
    aprConfigOptionsNew?.New?.options?.enableThirdPartyLenders ||
    aprConfigOptionsNew?.CPO?.options?.enableThirdPartyLenders ||
    aprConfigOptionsNew?.Used?.options?.enableThirdPartyLenders ||
    inStoreAprConfigOptionsNew?.New?.options?.enableThirdPartyLenders ||
    inStoreAprConfigOptionsNew?.CPO?.options?.enableThirdPartyLenders ||
    inStoreAprConfigOptionsNew?.Used?.options?.enableThirdPartyLenders;

  const handleChange = (data: { formData: FormData }) => {
    const oldFormData = { ...formState.formData };
    const formData = applyUserTriggeredDefaults(
      oldFormData,
      data.formData,
      dealer,
    );
    if (
      formData?.featureConfig?.incentives?.excludeIncentivesFromAffiliates &&
      thirdPartyLendersEnabled(
        formData.aprConfigOptionsNew,
        formData.inStoreAprConfigOptionsNew,
      )
    ) {
      PageMessage({
        type: MessageType.ERROR,
        message:
          'The option "Use third party lenders" ("Pricing" -> "Online APR Configuration" or "In-Store APR Configuration" ->"New" or "Used" ->  "Use Rates from AIS") or ("Pricing" -> "Online Pricing Matrix" or "In-Store Pricing Matrix" -> "Certified Pre Owned" -> "Set APR" -> "Use Rates from AIS") should be disabled in order to enable "Exclude incentives from affiliates".',
      });

      // Revert change
      processFormHandler(oldFormData as FormData);
      return;
    }

    // If Default Pencil was unchecked,
    // Set Desking Manager true
    if (
      !formData?.desking?.defaultPencil &&
      oldFormData?.desking?.defaultPencil !== formData?.desking?.defaultPencil
    ) {
      set(formData, 'desking.managerDesking', true);
      setFormState({ ...formState, ...formData });
    }

    if (
      oldFormData.countryToggle &&
      formData.countryToggle &&
      oldFormData.countryToggle !== formData.countryToggle
    ) {
      // If countryToggle has changed
      // Challenge the user to continue
      ChangeCountryChallengeModal({
        value: formData.countryToggle,
      }).then((confirmChange: boolean) => {
        if (confirmChange) {
          // User really wants to change country
          changeCountry(formData);
        } else {
          // Revert change
          processFormHandler(oldFormData as FormData);
        }
      });
    } else if (!oldFormData.countryToggle && formData.countryToggle) {
      changeCountry(formData);
    } else {
      processFormHandler(formData);
    }
  };

  const handleBlur = (fieldName: string, fieldValue: string) => {
    // if the zip is changed, try to fill in timezone
    if (fieldName === 'root_zip') {
      let timezone;
      if (formState.formData.country === COUNTRIES.CA) {
        timezone = postalCodeToTimezone(fieldValue);
      } else {
        timezone = zipcodeToTimezone.lookup(fieldValue);
      }
      if (timezone) {
        setFormState({
          ...formState,
          ...{ formData: { ...formState.formData, timezone } },
        });
      }
    }
  };

  const validate = (formData: FormData, errors: Errors) => {
    const { supportEmail } = formData;
    if (supportEmail && !validator.isEmail(supportEmail)) {
      errors.supportEmail.addError('Please enter a valid support email');
    }
    if (
      get(formData, 'insurance.insuranceEmail') &&
      !validator.isEmail(get(formData, 'insurance.insuranceEmail'))
    ) {
      errors.insurance.insuranceEmail.addError(
        'Please enter a valid insurance email',
      );
    }

    if (curStep === 1) {
      if (formData.zip && !validation.isZip(formData.zip)) {
        errors.zip.addError('Please enter a valid zip code');
      }
      // If the dealer is not independent, they must have a brand
      if (
        !formData.isIndependent &&
        (!formData.brand || formData.brand.length === 0)
      ) {
        errors.brand.addError('Please add a brand');
      }
      if (
        formData.privacyPolicyUrl &&
        !validator.isURL(formData.privacyPolicyUrl, {
          protocols: ['http', 'https'],
          require_protocol: true,
          require_host: true,
        })
      ) {
        errors.privacyPolicyUrl.addError(
          'Please enter a valid Privacy Policy URL',
        );
      }
    }

    if (curStep === 4) {
      const { crmType, tradeInManagerType } = formData;

      const hasCRMEmail =
        formData.crmEmail || (formData.crmEmailNew && formData.crmEmailUsed);
      if (crmType === CrmType.STANDALONE && !hasCRMEmail) {
        errors.crmType.addError(
          'ADF Email is required for standalone CRM Type.',
        );
      }

      if (crmType === CrmType.ELEAD && !formData.crmDefaults?.subscriptionId) {
        errors.crmDefaults.subscriptionId.addError(
          'Fortellis Subscription ID is required for Elead CRM Type.',
        );
      }

      if (crmType === CrmType.DRIVECENTRIC && !formData.drivecentricId) {
        errors.drivecentricId.addError(
          'DriveCentric ID is required for DriveCentric CRM Type.',
        );
      }

      if (!tradeInManagerType) {
        return errors;
      }
    }

    if (curStep === 5) {
      const managers = formData.managers || [];
      const needsEmail = (manager: Manager) =>
        manager.shouldReceiveSignatures ||
        manager.isPrimaryNewSigner ||
        manager.isPrimaryUsedSigner;
      const hasEmail = (manager: Manager) => manager.email;

      // Email must be entered for e-contracting emails to be received
      managers.forEach((manager, i) => {
        if (needsEmail(manager) && !hasEmail(manager)) {
          errors.managers[i].addError(
            'Email is required for e-contracting emails',
          );
        }
      });

      // We can only have one primary New car signer and one primary Used car signer.
      const newSignerArr = managers.map((m) => (m.isPrimaryNewSigner ? 1 : 0));
      if (sum(newSignerArr) > 1) {
        errors.managers.addError('Only one Primary New Car Signer is allowed');
      }
      const usedSignerArr = managers.map((m) =>
        m.isPrimaryUsedSigner ? 1 : 0,
      );
      if (sum(usedSignerArr) > 1) {
        errors.managers.addError('Only one Primary Used Car Signer is allowed');
      }
    }

    if (curStep === 7) {
      const element = document.querySelector(
        '#root_wizard_enableProdigyWeb .form-group.field.field-string',
      );
      if (
        formData?.wizard?.enableProdigyWeb?.enabled &&
        !formData?.wizard?.enableProdigyWeb?.newWebsiteId
      ) {
        errors.wizard.enableProdigyWeb = errors.wizard.enableProdigyWeb || {};
        errors.wizard.enableProdigyWeb.addError('Website must be selected');

        if (element) {
          element.classList.add('has-error', 'has-danger');
        }
      } else if (element) {
        element.classList.remove('has-error', 'has-danger');
      }

      const shouldUseKbbTradeIns =
        !isEmpty(formData?.featureConfig) &&
        !formData?.featureConfig?.kbbDefaultOption?.enabled &&
        !formData?.featureConfig?.acuTrade?.enabled &&
        !formData?.featureConfig?.kbbIco?.enabled &&
        !formData?.featureConfig?.blackBook?.enabled &&
        !formData?.featureConfig?.tradePending?.enabled &&
        !formData?.featureConfig?.tradePending?.showOnline;

      if (shouldUseKbbTradeIns) {
        errors.featureConfig.kbbDefaultOption.addError(
          `Please select KBB unless another trade-in type is configured to show online.`,
        );
      }

      if (
        hasDuplicateDownPaymentOptions(formData?.featureConfig?.downPayment)
      ) {
        errors.featureConfig.downPayment.downPaymentOptions.addError(
          'Duplicate down payment is not allowed.',
        );
      }

      const initialSoftCreditPull =
        processedFormFromProps.formData?.featureConfig?.softCreditPull;
      const initialUsername = initialSoftCreditPull?.username;
      const initialPassword = initialSoftCreditPull?.password;

      const currentSoftCreditPull = formData?.featureConfig?.softCreditPull;
      const currentUsername = currentSoftCreditPull?.username;
      const currentPassword = currentSoftCreditPull?.password;

      const hasSoftCreditChanges =
        currentUsername !== initialUsername ||
        currentPassword !== initialPassword;

      const softCreditErrors = errors.featureConfig.softCreditPull;
      if (hasSoftCreditChanges) {
        const bothEmpty = !currentUsername && !currentPassword;
        const bothPresent = !!currentUsername && !!currentPassword;

        if (!bothEmpty && !bothPresent) {
          if (!currentUsername) {
            softCreditErrors.username.addError(
              'Username is required unless you clear both fields to remove credentials.',
            );
          }
          if (!currentPassword) {
            softCreditErrors.password.addError(
              'Password is required unless you clear both fields to remove credentials.',
            );
          }
        }
      }
    }

    if (curStep === 8) {
      if (get(formData, 'wizard.hotLeads.emails')) {
        formData?.wizard?.hotLeads?.emails?.forEach((value, index) => {
          if (!validator.isEmail(value)) {
            errors.wizard.hotLeads.emails[index].addError(
              'Please enter a valid email',
            );
          }
        });
      }
    }
    return errors;
  };

  const transformErrors = (errors: Errors) =>
    filter(
      errors,
      (e) =>
        !(
          e.name === 'oneOf' ||
          e.property === '.featureConfig.ftpUpload.enabled'
        ),
    );

  const renderNav = () => (
    <div>
      <div className="stepwizard">
        <div className="stepwizard-row setup-panel">
          {rawSchemaList.map((schema: Schema, index) => {
            if (index === 0) {
              return false;
            }
            if (hideFeesTab && index === FEES_STEP) {
              return false;
            }
            return (
              <div key={schema.title} className="stepwizard-step">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  className={
                    index === getStep(step)
                      ? 'btn btn-primary btn-circle'
                      : 'btn btn-default btn-circle'
                  }
                  onClick={() => setStep(index)}
                >
                  {index}
                </a>
                <p>{schema.title}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderVerificationSection = () => {
    const renderItemList = [];
    if (formState.formData.fniType) {
      renderItemList.push(
        <IntegrationVerification
          key="fni"
          type="fni"
          label="Test F&I"
          dealer={formState.formData}
          onClick={actions.fniVerificationRequest}
        />,
      );
    }
    return renderItemList;
  };
  // Step 9, 12 are not managed by JSON Schema Plugin
  const isJsonSchemaTab = ![9, 12].includes(curStep);
  const isOwnerShipTab = curStep === 9;
  const isThemeTab = curStep === 12;
  const isLendingTab = curStep === 11;

  return (
    <div>
      <div
        className={`dealer-editor-container ${isValidating ? 'disabled' : ''}`}
      >
        <div>{renderNav()}</div>
        {isJsonSchemaTab && (
          <div className="dealer-editor-json-form-container">
            {formState.schema.title === 'Inventory' && (
              <div className="form-inventory-actions">
                <Confirm
                  onConfirm={() => onInventoryImport(dealer.id)}
                  body={`Are you sure you want to import the inventory feeds for ${dealer.name}?`}
                  confirmText="Confirm Import"
                  title={`Importing inventory for ${dealer.name}`}
                >
                  <Button className="form-inventory-import btn-primary">
                    Import Inventory
                  </Button>
                </Confirm>
              </div>
            )}
            {
              // @ts-ignore @TODO fix
              <Form
                fields={{
                  percent: PercentField,
                  MultiSchemaField: ProdigyMultiSchemaField,
                }}
                schema={formState.schema}
                uiSchema={formState.uiSchema}
                widgets={{
                  TextWidget: UARCustomTextWidget,
                  PasswordWidget: UARCustomPasswordWidget,
                }}
                formData={formState.formData}
                formContext={{
                  isIndependent: formState.formData.isIndependent,
                }}
                transformErrors={transformErrors}
                validate={validate}
                onSubmit={onSubmit}
                onChange={handleChange}
                onBlur={handleBlur}
                enctype="multipart/form-data"
                autoComplete="off"
                ref={(form: any) => {
                  setFormRef(form);
                }}
              >
                {/* BEGIN NON-JSON SCHEMA CONFIG */}
                {curStep === 7 && (
                  <div className="form-group field field-object col-md-12 config-group">
                    <fieldset id="root_wizard_enableProdigyWeb">
                      <legend id="root_wizard_enableProdigyWeb__title">
                        Upstart Web
                      </legend>
                      <div className="form-group field field-boolean col-md-12">
                        <label
                          className="control-label"
                          htmlFor="root_wizard_enableProdigyWeb_enabled"
                        >
                          Enable Upstart Web
                        </label>
                        <Toggle
                          size="sm"
                          className="toggle-switch"
                          active={
                            !!formState.formData.wizard?.enableProdigyWeb
                              ?.enabled
                          }
                          // once it is enable, it cannot be turned it off
                          disabled={
                            !!formState.formData.wizard?.enableProdigyWeb
                              ?.enabled
                          }
                          onClick={(value: boolean) => {
                            onProdigyWebsiteEnableChange(value);
                            populateWebsiteListIfEmpty(value);
                          }}
                        />
                      </div>
                      {formState.formData.wizard?.enableProdigyWeb?.enabled && (
                        <div className="form-group field field-string">
                          <label
                            className="control-label"
                            htmlFor="root_wizard_enableProdigyWeb_newWebsiteId"
                          >
                            Default Website ID
                            <span className="required">*</span>
                          </label>
                          <select
                            id="root_wizard_enableProdigyWeb_newWebsiteId"
                            className="form-control"
                            name="newWebsiteId"
                            value={
                              formState.formData.wizard?.enableProdigyWeb
                                ?.newWebsiteId
                            }
                            onChange={(e) => {
                              onProdigyWebsiteChange(e.target.value);
                            }}
                            required
                          >
                            <option value="">None</option>
                            {websites.map((website: Website) => (
                              <option key={website.id} value={website.id}>
                                {website.id}
                              </option>
                            ))}
                          </select>
                          <div className="required-warning">
                            <ul className="error-detail bs-callout bs-callout-info">
                              <li className="text-danger">
                                is a required property
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </fieldset>
                  </div>
                )}
                {/* END NON-JSON SCHEMA CONFIG */}
                {/* ARL Lending Config - shown on Lending tab when Upstart Standalone Lending is enabled */}
                {isLendingTab &&
                  formState.formData?.lending?.upstart?.standaloneLending
                    ?.enabled && (
                    <div className="form-group field field-object col-md-12 arl-lending-config-group">
                      <ARLLendingConfig
                        dealerId={dealer.id}
                        onBack={onBack}
                        fetching={fetching}
                        hideButtons
                      />
                    </div>
                  )}
                <button
                  ref={(btn: any) => {
                    setSubmitButtonRef(btn);
                  }}
                  className="hidden"
                />
              </Form>
            }
          </div>
        )}
        {isOwnerShipTab && (
          <OwnershipOptionsConfig
            onBack={onBack}
            actions={actions}
            dealer={dealer}
            fetching={fetching}
          />
        )}
        {isThemeTab && <Theme dealer={dealer} currentTab="dealer" />}

        {formState.schema.title === 'Integrations' &&
          renderVerificationSection()}
        {isJsonSchemaTab && (
          <ButtonGroup>
            {curStep > START_STEP && (
              <Button
                disabled={fetching || isValidating}
                type="submit"
                onClick={onBack}
              >
                {'<< Back'}
              </Button>
            )}
            <Button
              bsClass="btn btn-primary btn-large"
              disabled={fetching || isValidating}
              type="submit"
              onClick={submitAndExit}
            >
              Save & Exit
            </Button>
            <Button
              bsClass="btn btn-primary btn-large"
              disabled={fetching || isValidating}
              type="submit"
              onClick={submitForm}
            >
              {'Save >>'}
            </Button>
          </ButtonGroup>
        )}
        {isValidating && <div>Validating credentials...</div>}
      </div>
    </div>
  );
};

const mapStateToProps = (state: object) => ({
  tmpDealer: getTmpDealer(state),
  websites: getWebsites(state),
});

export default connect(mapStateToProps)(DealerEditor);
